<template>
  <div v-if="finalMessage">
    <heading><small>{{finalMessage.heading}}</small></heading>
    <form-message v-if="finalMessage.error" severity="warning">
      Whoops, you've found an error:

      <code class="error-message">
        {{finalMessage.error}}
      </code>

      This might be our fault, if so we are sorry.
    </form-message>
    <p v-if="finalMessage.error" class="text-center">
      <router-link to="/logout">Signing in again</router-link> might help.
    </p>
    <form-message v-else severity="information">
      {{finalMessage.text || ''}}
      Open WElix sessions will expire soon but you can end them now by clicking
      log out in the application if you prefer.
    </form-message>
  </div>
  <div v-else class="account">
    <heading><small>My account</small></heading>

    <form-message v-if="privacyNoticeUpdated"
                  severity="information">
      The privacy notice has changed since your last review. Click
      <router-link to="/ask-consent">here</router-link> to review and consent to
      the updated notice.
    </form-message>

    <h2 class="section">Profile</h2>
    <table>
      <tr>
        <th class="text-left">Email / login:</th>
        <td class="text-right">{{profile.email}}</td>
      </tr>
      <tr>
        <th class="text-left">Name:</th>
        <td class="text-right">{{profile.given_name}} {{profile.family_name}}</td>
      </tr>
    </table>

    <p class="text-center">
      <form-button @click="$router.push('/logout')">
        Sign out
      </form-button>
      <form-button @click="$router.push('/edit')">
        Edit profile
      </form-button>
      <form-button @click="$router.push(`/reset/${profile.email}`)">
        Change password
      </form-button>
      <form-button v-if="privacyNoticeUpdated"
                   color="blue"
                   @click="$router.push('/ask-consent')">
        Review updated privacy notice
      </form-button>
      <form-button v-else
                   @click="$router.push('/privacy-notice')">
        View privacy notice
      </form-button>
    </p>

    <h2 class="section">Recent/active sessions</h2>
    <p>
      This list shows all the devices that have signed in with your account
      recently.  If something here looks suspicious to you, you should
      <router-link :to="`/reset/${profile.email}`">change your password</router-link>
      and sign out everywhere.
    </p>
    <form-message severity="warning" :message="sessionsError" />
    <div class="wrap-mobile">
      <table>
        <tr class="row-header">
          <th>Browser/device</th>
          <th>Last login</th>
        </tr>
        <tr v-for="session in sessions">
          <td>
            {{session.device.browser}}
            on
            {{session.device.model || session.device.os}}
            {{session.device.type || ''}}
          </td>
          <td>
            {{session.lastLoginRelative}}
          </td>
        </tr>
      </table>
    </div>
    <p class="text-center">
      <form-button v-if="fetchMoreSessions" @click="fetchMoreSessions">
        load more sessions
      </form-button>
      <form-button @click="clearSessions">
        Sign out everywhere
      </form-button>
    </p>

    <h2 class="section">Danger zone</h2>
    <p>
      If you delete your account you will no longer be able to sign in to WElix,
      and your access will be revoked in all WElix instances.
    </p>
    <form-message v-if="deleteConfirmed"
                  severity="warning">
      Are you sure you want to delete your account? There is no way back from this!
    </form-message>
    <p class="text-center">
      <form-button v-if="!deleteConfirmed"
                   color="orange"
                   @click="deleteConfirmed = true">
        Delete account
      </form-button>
      <form-button v-if="deleteConfirmed"
                   color="orange"
                   @click="deleteUser">
        Yes I'm sure!
      </form-button>
      <form-button v-if="deleteConfirmed"
                   @click="deleteConfirmed = false">
        Wait, no!
      </form-button>
    </p>
  </div>
</template>

<script>
import { privacyNoticeDate } from '@/config';
import {
  getUserProfile,
  getConsentDate,
  listSessions,
  globalLogout,
  deleteUser,
} from '../auth';

import Heading from './elements/Heading';
import FormButton from './elements/FormButton';
import FormField from './elements/FormField';
import FormMessage from './elements/FormMessage';


export default {
  components: {
    Heading,
    FormButton,
    FormField,
    FormMessage,
  },

  data() {
    return {
      privacyNoticeUpdated: false,
      deleteConfirmed: false,
      profile: {},
      sessions: [],
      fetchMoreSessions: null,
      sessionsError: null,
      finalMessage: null,
    };
  },

  created() {
    getConsentDate((date) => {
      this.privacyNoticeUpdated = !date || privacyNoticeDate > date;
    });

    getUserProfile({
      success: (result) => {
        this.profile = result;
      },
      error: (error) => {
        this.finalMessage = {
          error,
          heading: 'Profile error',
        };
      },
    });

    listSessions({
      count: 10,
      success: ({ sessions, fetchMore }) => {
        this.sessions.push(...sessions);
        this.fetchMoreSessions = fetchMore;
      },
      error: (error) => {
        this.sessionsError = error;
      },
    });
  },

  props: {
    email: { type: String, default: '' },
  },

  computed: {
    msgSeverity() {
      return this.error ? 'warning' : 'information';
    },
  },

  methods: {
    clearSessions() {
      globalLogout({
        error: (error) => {
          this.finalMessage = {
            error,
            heading: 'Sign out failed',
          };
        },
        success: () => {
          this.finalMessage = {
            heading: 'Signed out',
            text: 'All sessions have been invalidated.',
          };
        },
      });
    },

    deleteUser() {
      deleteUser({
        error: (error) => {
          this.finalMessage = {
            error,
            heading: 'Delete failed',
          };
        },
        success: () => {
          this.finalMessage = {
            heading: 'Account deleted',
            text: 'Your account has been deleted and all permissions are now revoked.',
          };
        },
      });
    },
  },
};
</script>

<style scoped>
.account {
  text-align: justify;
}

table {
  text-align: left;
  width: 100%;
  margin: 0 0 10px;
  border-spacing: 0;
  border-collapse: collapse;
}
th,
td {
  padding: 10px 2px 2px;
  vertical-align: top;
}
.row-header > th {
  border-bottom: 1px solid #777;
}

.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}
.no-wrap {
  white-space: nowrap;
}
.message,
.text-center {
  text-align: center;
}

.error-message {
  display: block;
  margin: 10px 0;
}

.wrap-mobile {
  width: 100%;
  overflow-x: auto;
}
.wrap-mobile > table {
  min-width: 350px;
}

.section {
  margin-top: 50px;
  clear: both;
}
</style>



// WEBPACK FOOTER //
// src/components/Profile.vue